var _createClass = function () { function defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } } return function (Constructor, protoProps, staticProps) { if (protoProps) defineProperties(Constructor.prototype, protoProps); if (staticProps) defineProperties(Constructor, staticProps); return Constructor; }; }();

var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _possibleConstructorReturn(self, call) { if (!self) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return call && (typeof call === "object" || typeof call === "function") ? call : self; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function, not " + typeof superClass); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, enumerable: false, writable: true, configurable: true } }); if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass; }

import React, { Component, createContext } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';

import { getTranslation as _getTranslation, convertTranslationsFromLocPlatform } from '@nike/i18n-core';

// ////////////////////////////////////////
// CUSTOM PROP VALIDATION
// ////////////////////////////////////////

/**
 * Ensures that the translations prop is in the style expected when coming
 * from the JSON endpoint on the LocTool
 */
export var validateLocPlatformTranslationsProp = function validateLocPlatformTranslationsProp(props, propName) {
  var errorMessage = 'When fromLocPlatform is true, the translations prop must be an object' + 'in the following format:\n' + '{\n  results: 1,\n  translations: [{\n    en:"Value",\n    key: "some-key"\n  }]\n}';

  // eslint-disable-next-line react/destructuring-assignment
  var thisProp = props[propName];

  // Error immediately if we weren't given an object
  if ((typeof thisProp === 'undefined' ? 'undefined' : _typeof(thisProp)) !== 'object' && !Array.isArray(thisProp)) {
    throw new Error(errorMessage);
  }

  var translations = thisProp.translations;

  // Ensure there's a translations array

  if (!translations || !Array.isArray(translations)) {
    throw new Error(errorMessage);
  }

  // Loop through each translation and ensure it has a key prop
  translations.forEach(function (t, idx) {
    if (!t.key) {
      throw new Error('When fromLocPlatform is true, each translation object must ' + ('contain a "key" prop. None was found in the string object at index ' + idx + '.'));
    }
  });

  return null;
};

/**
 * Ensures our translations prop matches the format expected by the module
 * (and after it has been converted from the LocTool)
 */
export var validateConvertedTranslationsProp = function validateConvertedTranslationsProp(props, propName) {
  var errorMessage = 'When fromLocPlatform is false, the translations prop must be an object ' + 'in the following format:\n' + '{\n  "some-key":{\n    "en": "English Value",\n    "it": "Valore Italiano",\n  }\n}';

  // Our translations prop value
  // eslint-disable-next-line react/destructuring-assignment
  var thisProp = props[propName];

  // Error immediately if we weren't given an object
  if ((typeof thisProp === 'undefined' ? 'undefined' : _typeof(thisProp)) !== 'object' && !Array.isArray(thisProp)) {
    throw new Error(errorMessage);
  }

  // Loop through each string key and evaluate its value
  Object.keys(thisProp).forEach(function (stringKey) {
    // Ensure each string key's value is an object and NOT an array
    if (_typeof(thisProp[stringKey]) !== 'object' || Array.isArray(thisProp[stringKey])) {
      throw new Error(errorMessage);
    }

    // Loop each string key value and ensure it's a string
    Object.keys(thisProp[stringKey]).forEach(function (langTag) {
      if (typeof thisProp[stringKey][langTag] !== 'string') {
        throw new Error(errorMessage);
      }
    });
  });

  return null;
};

/**
 * Validates the incoming translation prop. It can either be from the LocPlatform
 * or it can be without doing the conversion.
 */
export var validateTranslationsProp = function validateTranslationsProp(props, propName) {
  // Allowing for an empty translations prop
  var translations = props[propName];
  if (!translations || !Object.keys(translations).length) {
    return null;
  }

  // Validate as if it's coming from the LocPlatform
  if (props.fromLocPlatform) {
    return validateLocPlatformTranslationsProp(props, propName);
  }

  // Validate assuming it's already converted
  return validateConvertedTranslationsProp(props, propName);
};

/**
 * Ensures that the passed currentLanguageTag has a matching object within
 * the supported languages prop
 */
export var validateCurrentLanguageTag = function validateCurrentLanguageTag(props) {
  var currentLanguageTag = props.currentLanguageTag,
      supportedLanguages = props.supportedLanguages;

  // Find the matching object

  var currentLanguage = supportedLanguages.find(function (l) {
    return l.tag === currentLanguageTag;
  });

  // Error if no match was found
  if (!currentLanguage) {
    var langOpts = supportedLanguages.map(function (l) {
      return l.tag;
    });
    throw Error('The currentLanguageTag prop "' + currentLanguageTag + '" doesn\'t match ' + ('any objects passed in your supportedLanguages prop. Options are: ' + langOpts.join(', ')));
  }

  return null;
};

// ////////////////////////////////////////
// THE COMPONENT
// ////////////////////////////////////////

export var defaultProviderState = {
  currentLanguageTag: 'pseudo',
  fromLocPlatform: true,
  supportedLanguages: [{
    name: 'Pseudo (Development)',
    tag: 'pseudo'
  }, {
    name: 'Primary Value (Development)',
    tag: 'primary-value'
  }],
  translations: {},
  defaultElement: 'span'
};

// Namespace and export the createContext() invocation to use with react hook useContext()
export var NikeI18nContext = createContext(defaultProviderState);

// Creates a Consumer/Provider pair
var NikeI18nConsumer = NikeI18nContext.Consumer,
    Provider = NikeI18nContext.Provider;
export { NikeI18nConsumer, Provider };


export var NikeI18nProvider = function (_Component) {
  _inherits(NikeI18nProvider, _Component);

  function NikeI18nProvider() {
    var _ref;

    var _temp, _this, _ret;

    _classCallCheck(this, NikeI18nProvider);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = _possibleConstructorReturn(this, (_ref = NikeI18nProvider.__proto__ || Object.getPrototypeOf(NikeI18nProvider)).call.apply(_ref, [this].concat(args))), _this), _this.getProviderValue = function () {
      var _this$props = _this.props,
          currentLanguageTag = _this$props.currentLanguageTag,
          defaultElement = _this$props.defaultElement,
          fromLocPlatform = _this$props.fromLocPlatform,
          supportedLanguages = _this$props.supportedLanguages,
          translations = _this$props.translations;

      // We need to convert the incoming translations prop to the expected format
      // if we're coming from the LocPlatform, otherwise we can assume the
      // prop doesn't need to be converted

      var finalTranslations = fromLocPlatform && !isEmpty(translations) ? convertTranslationsFromLocPlatform(translations) : translations;

      return {
        // We're guaranteed for this to return a value due to the custom prop validation
        currentLanguage: supportedLanguages.find(function (l) {
          return l.tag === currentLanguageTag;
        }),
        defaultElement: defaultElement,
        supportedLanguages: supportedLanguages,
        translations: finalTranslations,

        // The primary function used to get an i18nString
        i18nString: function i18nString(_ref2) {
          var description = _ref2.description,
              icuOptions = _ref2.icuOptions,
              icuTokens = _ref2.icuTokens,
              primaryValue = _ref2.primaryValue,
              stringKey = _ref2.stringKey;
          return _getTranslation(stringKey, currentLanguageTag, primaryValue, description, finalTranslations[currentLanguageTag] || {}, icuTokens, icuOptions);
        },

        // ---- DEPRECATED ----
        getTranslation: function getTranslation(_ref3) {
          var description = _ref3.description,
              icuOptions = _ref3.icuOptions,
              icuTokens = _ref3.icuTokens,
              primaryValue = _ref3.primaryValue,
              stringKey = _ref3.stringKey;
          return _getTranslation(stringKey, currentLanguageTag, primaryValue, description, finalTranslations[currentLanguageTag] || {}, icuTokens, icuOptions);
        }
      };
    }, _temp), _possibleConstructorReturn(_this, _ret);
  }

  _createClass(NikeI18nProvider, [{
    key: 'render',
    value: function render() {
      var children = this.props.children;


      return React.createElement(
        Provider,
        { value: this.getProviderValue() },
        children
      );
    }
  }]);

  return NikeI18nProvider;
}(Component);

NikeI18nProvider.propTypes = {
  children: PropTypes.node.isRequired,
  currentLanguageTag: validateCurrentLanguageTag,
  fromLocPlatform: PropTypes.bool,
  supportedLanguages: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    tag: PropTypes.string.isRequired
  })),
  translations: validateTranslationsProp,
  defaultElement: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.elementType])
};
NikeI18nProvider.defaultProps = defaultProviderState;
export default NikeI18nProvider;